<template>
    <div>
         <label
             v-if="$slots.default"
             :for="'input' + _uid"
             class="mb-8 text-body-sm flex items-center"
         >
                 <span class="flex flex-col items-start w-full">
                     <span class="first-letter:uppercase font-semibold text-black">
                         <slot></slot>
                     </span>
                     <span v-if="$slots.description" class="text-primary-80 text-body-sm">
                         <slot name="description"></slot>
                     </span>
                 </span>
         </label>

         <slot name="tip"></slot>

         <div
             :class="{'border-semantic-error': error,
              'border-semantic-info': editorHasFocus,
              'border-primary-20 bg-primary-5': disabled,
              'border-primary-80': !editorHasFocus && !error && !disabled
              }"
             class="border rounded bg-white overflow-hidden"
         >
             <div
                 ref="focusTarget"
                 class="rounded-t editor outline-none"
                 tabindex="0"
                 @focus="focusEditor"
             >
                 <div
                     v-if="editor"
                     :class="[disabled ? 'border-primary-20' : 'border-primary-60']"
                     class="border-b"
                 >
                     <div class="flex items-center gap-12 pt-16 pl-16 mb-16">

                         <placeholder-button
                             v-if="placeholders.length"
                             :categories="placeholders"
                             @select="insertPlaceholder"
                         />

                         <div v-if="placeholders.length" class="h-24 border-l border-primary-20"></div>

                         <select-dropdown
                             v-model="headingSelectedOption"
                             :options="headingOptions"
                             :disabled="disabled"
                             @change="handleText"
                         />

                         <editor-button
                             v-if="editor.commands.setBold"
                             :active="editor.isActive('bold')"
                             @click="editor.commands.toggleBold()"
                         >
                             <svg-icon class="w-24 h-24" type="format-bold"></svg-icon>
                         </editor-button>

                         <editor-button
                             v-if="editor.commands.setItalic"
                             :active="editor.isActive('italic')"
                             @click="editor.commands.toggleItalic()"
                         >
                             <svg-icon class="w-24 h-24" type="format-italic"></svg-icon>
                         </editor-button>

                         <editor-button
                             v-if="editor.commands.toggleUnderline"
                             :active="editor.isActive('underline')"
                             @click="editor.commands.toggleUnderline()"
                         >
                             <svg-icon class="w-24 h-24" type="format-underlined"></svg-icon>
                         </editor-button>

                         <editor-button
                             v-if="editor.commands.toggleStrike"
                             :active="editor.isActive('strike')"
                             @click="editor.commands.toggleStrike()"
                         >
                             <svg-icon class="w-24 h-24" type="format-strikethrough"></svg-icon>
                         </editor-button>

                         <editor-button
                             v-if="editor.commands.toggleBulletList"
                             :active="editor.isActive('bulletList')"
                             @click="editor.commands.toggleBulletList()"
                         >
                             <svg-icon class="w-24 h-24" type="format-list-bulleted"></svg-icon>
                         </editor-button>

                         <editor-button
                             v-if="editor.commands.toggleOrderedList"
                             :active="editor.isActive('orderedList')"
                             @click="editor.commands.toggleOrderedList()"
                         >
                             <svg-icon class="w-24 h-24" type="format-list-numbered"></svg-icon>
                         </editor-button>

                         <div v-if="editor.commands.toggleLink">
                             <editor-button
                                 :active="editor.isActive('link')"
                                 @click="showLinkPrompt(editor.getAttributes('link'))"
                             >
                                 <svg-icon class="w-24 h-24" type="link"></svg-icon>
                             </editor-button>

                             <popup-modal
                                 v-if="insertingLink"
                                 :has-close-button="false"
                                 position="center"
                                 @close="hideLinkPrompt()"
                             >
                                 <form @submit.prevent="insertLink(editor.commands.setLink)">
                                     <simple-input ref="linkInput" v-model="linkUrl" class="w-320 mb-16">
                                         {{ $t('wysiwyg.input_link') }}
                                     </simple-input>

                                     <div class="flex">
                                         <simple-button class="ml-auto mr-8" @click="hideLinkPrompt()">
                                             {{ $t('wysiwyg.cancel') }}
                                         </simple-button>
                                         <simple-button display="secondary" look="primary" type="submit">
                                             {{ $t('wysiwyg.save') }}
                                         </simple-button>
                                     </div>
                                 </form>

                             </popup-modal>
                         </div>

                         <div v-if="editor.commands.setImage">
                             <editor-button @click="showImagePrompt()">
                                 <svg-icon class="w-24 h-24" type="image"></svg-icon>
                             </editor-button>

                             <popup-modal
                                 v-if="insertingImage"
                                 :has-close-button="false"
                                 position="center"
                                 @close="hideImagePrompt()"
                             >
                                 <form @submit.prevent="insertImage(editor.commands.setImage)">
                                     <simple-input ref="imageInput" v-model="imageUrl" class="w-320 mb-16">
                                         {{ $t('wysiwyg.input_image') }}
                                     </simple-input>

                                     <div class="flex">
                                         <simple-button class="ml-auto mr-8" @click="hideImagePrompt()">
                                             {{ $t('wysiwyg.cancel') }}
                                         </simple-button>
                                         <simple-button display="secondary" look="primary" type="submit">
                                             {{ $t('wysiwyg.insert') }}
                                         </simple-button>
                                     </div>
                                 </form>

                             </popup-modal>
                         </div>

                         <emoji-button @select="insertEmoji"></emoji-button>

                         <editor-button v-if="editor.commands.undo" @click="editor.commands.undo()">
                             <svg-icon class="w-24 h-24" type="undo"></svg-icon>
                         </editor-button>

                         <editor-button v-if="editor.commands.redo" @click="editor.commands.redo()">
                             <svg-icon class="w-24 h-24" type="redo"></svg-icon>
                         </editor-button>
                     </div>
                 </div>

                 <editor-content
                     v-if="editor"
                     :editor="editor"
                     class="editor__content"
                     :class="useMinHeight ? 'minHeight' : ''"
                 />

             </div>

             <slot name="after"></slot>

         </div>

         <div v-if="maxSize"
              :class="{'text-semantic-error font-semibold' : editorCharacterLength >= maxSize}"
              class="flex justify-end">
             <div>
                 {{ editorCharacterLength }} / {{ maxSize }}
             </div>
         </div>

         <p v-if="error" class="text-semantic-error text-body-sm">{{ error }}</p>

     </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-3';
import Blockquote from '@tiptap/extension-blockquote';
import Bold from '@tiptap/extension-bold';
import BulletList from '@tiptap/extension-bullet-list';
import Code from '@tiptap/extension-code';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import Heading from '@tiptap/extension-heading';
import History from '@tiptap/extension-history';
import Image from '@tiptap/extension-image';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import Strike from '@tiptap/extension-strike';
import Text from '@tiptap/extension-text';
import Underline from '@tiptap/extension-underline';
import SvgIcon from '../ui/SvgIcon.vue';
import EditorButton from '../ui/EditorButton.vue';
import SimpleDropdown from '../ui/SimpleDropdown.vue';
import PopupModal from '../ui/PopupModal.vue';
import Input from '../forms/Input.vue';
import SimpleButton from '../ui/SimpleButton.vue';
import EmojiButton from '../ui/EmojiButton.vue';
import PlaceholderButton from '../ui/PlaceholderButton.vue';
import SelectDropdown from "./SelectDropdown.vue";

export default {
   components: {
       SelectDropdown,
       'editor-content': EditorContent,
       'svg-icon': SvgIcon,
       'editor-button': EditorButton,
       'simple-dropdown': SimpleDropdown,
       'popup-modal': PopupModal,
       'simple-input': Input,
       'simple-button': SimpleButton,
       'emoji-button': EmojiButton,
       'placeholder-button': PlaceholderButton,
   },
   props: {
       hideActions: { type: Boolean, default: false },
       fullFeatured: Boolean,
       withLinks: Boolean,
       value: String,
       autofocus: Boolean,
       placeholders: {
           type: Array,
           default: () => [],
           required: false,
       },
       useMinHeight: {type: Boolean,default: true},
       maxSize: {
           required: false,
           default: null,
       },
       withListItem: {
           // show the button
           type: Boolean,
           default: true,
       },
       errors: Array,
       disabled: Boolean,
   },
   data: function () {
       return {
           editor: null,
           selectedType: 'p',
           insertingImage: false,
           insertingLink: false,
           linkUrl: '',
           imageUrl: '',
           headingSelectedOption: 0,
           headingOptions: [
               {value: 0, label: 'wysiwyg.p'},
               {value: 1, label: 'wysiwyg.h1'},
               {value: 2, label: 'wysiwyg.h2'},
           ],
       };
   },
   computed: {
       editorHasFocus() {
           return this.editor && this.editor.focused && !this.disabled;
       },
       editorCharacterLength() {
           return this.value.length;
       },
       error() {
           if (!this.errors) {
               return null;
           }

           return Array.isArray(this.errors) ? this.errors[0] : this.errors;
       },
   },
   methods: {
       handleText(event) {
           event === 0
               ? this.editor.chain().focus().setParagraph().run()
               : this.editor.chain().focus().toggleHeading({ level: event}).run();
       },
       showLinkPrompt(attrs) {
           this.linkUrl = attrs.href || '';
           this.insertingLink = true;

           this.$nextTick(() => {
               this.$refs.linkInput.focus();
           });
       },
       hideLinkPrompt() {
           this.insertingLink = false;
       },
       insertLink(command) {
           command({href: this.linkUrl});
           this.hideLinkPrompt();
           this.linkUrl = '';
       },
       showImagePrompt() {
           this.insertingImage = true;

           this.$nextTick(() => {
               this.$refs.imageInput.focus();
           });
       },
       hideImagePrompt() {
           this.insertingImage = false;
       },
       insertImage(command) {
           command({src: this.imageUrl});
           this.hideImagePrompt();
           this.imageUrl = '';
       },
       focusEditor() {
           if (!this.disabled) {
               this.editor.chain().focus();
           }
       },
       insertEmoji(emoji) {
           this.editor.commands.insertContent(emoji);
       },
       insertPlaceholder(placeholder) {
           this.editor.chain().focus().command(({tr}) => {
               tr.insertText('{{ ' + placeholder + ' }} ');
               return true;
           }).run();
       },
       setContent(content) {
           this.editor.commands.setContent(content);
           this.editor.chain().focus();
           this.$emit('input', content);
       },
   },
   watch: {
       value(value) {
           // set content to empty string instead of an empty paragraph
           if (value === '<p></p>') {
               this.setContent('');
           }
       },
       disabled(value) {
           if (this.editor) {
               this.editor.setOptions({editable: !value});
           }
       },
   },
   mounted() {
       let extensions = [
           Placeholder.configure({
               emptyNodeClass: 'is-empty',
               placeholder: this.$t('wysiwyg.start_writing') + ' …',
           }),
           Heading.configure({
               levels: [1, 2, 3],
           }),
           Underline,
           Strike,
           Bold,
           Italic,
           History,
           HardBreak,
           Text,
           Document,
           Paragraph,
       ];

       if (this.withListItem) {
           extensions = [
               ...extensions,
               BulletList,
               OrderedList,
               ListItem,
           ];
       }

       if (this.fullFeatured) {
           extensions = [
               ...extensions,
               Code,
               Link,
               Image,
               Blockquote,
           ];
       } else if (this.withLinks) {
           extensions.push(Link);
       }

       this.editor = new Editor({
           editable: !this.disabled,
           content: this.value,
           autoFocus: this.autofocus ? 'end' : false,
           parseOptions: {
               preserveWhitespace: 'full',
           },
           disableInputRules: true,
           disablePasteRules: true,
           extensions,
           onUpdate: ({ editor }) => {
               this.$emit('input', editor.getHTML());
           },
           onFocus: () => {
               this.$emit('focus');
           },
           editorProps: {
               handleTextInput: (view) => {
                   if (this.maxSize && this.value.length >= this.maxSize && view.state.selection.empty) {
                       return true;
                   }
               },
               handlePaste: (view, event, slice) => {
                   if (this.maxSize && this.value.length + slice.size > this.maxSize) {
                       slice.content = new DocumentFragment();
                       return true;
                   }
               },
           },
       });
       this.editor.commands.setContent(this.value)
   },
   beforeUnmount() {
       this.editor.destroy();
   },
};

</script>

<style>
.tiptap.ProseMirror {
    line-height: 1.5 !important;
}

.editor__content p.is-empty:first-child::before {
    @apply text-primary-80;
    content: attr(data-empty-text);
    float: left;
    pointer-events: none;
    height: 0;
    font-style: italic;
}

.editor__content .ProseMirror {
    @apply p-16 outline-none text-body-sm;
}
.editor__content.minHeight .ProseMirror {
    @apply min-h-240;
}

.ProseMirror {
&  h1,
&  h2,
&  h3,
&  p,
&  ul,
&  ol,
&  pre,
&  blockquote {
       @apply mt-8;

&:first-child {
     @apply mt-0;
 }
}

& h1 {
      @apply font-semibold text-heading-1;
  }

& h2 {
      @apply font-semibold text-heading-2;
  }

& ul {
      @apply list-disc pl-20;
  }

& ol {
      @apply list-decimal pl-20;
  }

& a {
      @apply text-primary-60 underline;
  }

}
</style>
